import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ChevronRight, Sparkles, Plus, Video } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/** Home page Property Reels section — header + Post Reel + trending strip. */
export default function ReelsHomeStrip({ city = null }) {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get("/reels/feed", { params: { tab: "trending", limit: 8, city } })
            .then((r) => setItems(r.data?.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoaded(true));
    }, [city]);

    if (!loaded) return null;

    const postReelHref = user ? "/reels/create" : "/login?next=/reels/create";

    return (
        <section className="px-4 mt-4" data-testid="mnh-reels-strip">
            {/* Header */}
            <div className="flex items-center justify-between mb-2 gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                         style={{ background: "linear-gradient(135deg, #DAA520, #F59E0B)" }}>
                        <Sparkles className="w-3 h-3" style={{ color: "#001a3d" }} />
                    </div>
                    <div className="font-heading font-extrabold text-[13px] truncate" style={{ color: "#002657" }}>Property Reels</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    {items.length > 0 && (
                        <Link to="/reels" className="inline-flex items-center gap-0.5 text-[11px] font-bold px-2 py-1"
                              style={{ color: "#2563EB" }} data-testid="mnh-reels-all">
                            View All <ChevronRight className="w-3 h-3" />
                        </Link>
                    )}
                    <Link
                        to={postReelHref}
                        data-testid="mnh-reels-post"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-extrabold shadow-sm"
                        style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                    >
                        <Plus className="w-3 h-3" /> Post Reel
                    </Link>
                </div>
            </div>

            {items.length === 0 ? (
                <Link
                    to={postReelHref}
                    data-testid="mnh-reels-empty-cta"
                    className="block rounded-2xl border-2 border-dashed p-4 text-center"
                    style={{ borderColor: "rgba(218,165,32,0.4)", background: "#FBF6E0" }}
                >
                    <div className="w-10 h-10 rounded-full mx-auto flex items-center justify-center mb-2"
                         style={{ background: "linear-gradient(135deg, #DAA520, #F59E0B)" }}>
                        <Video className="w-5 h-5 text-white" />
                    </div>
                    <div className="font-heading font-extrabold text-[12.5px]" style={{ color: "#002657" }}>
                        Be the first to post a Property Reel
                    </div>
                    <div className="text-[10.5px] mt-0.5" style={{ color: "#8B6712" }}>
                        Discover properties through short videos
                    </div>
                </Link>
            ) : (
                <div className="overflow-x-auto no-scrollbar -mx-4 px-4">
                    <div className="flex gap-2">
                        {items.map((r) => (
                            <Link
                                key={r.id}
                                to={`/reels/${r.id}`}
                                data-testid={`mnh-reel-${r.id}`}
                                className="relative shrink-0 w-[120px] h-[190px] rounded-xl overflow-hidden bg-black shadow"
                            >
                                {r.thumbnail_url ? (
                                    <img src={fileUrl(r.thumbnail_url)} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                                ) : (
                                    <video src={fileUrl(r.video_url)} muted playsInline className="absolute inset-0 w-full h-full object-cover" />
                                )}
                                <div className="absolute inset-0 flex items-end p-2"
                                     style={{ background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.85))" }}>
                                    <div className="w-full">
                                        <div className="flex items-center gap-1 text-white text-[9px] font-bold mb-1">
                                            <Play className="w-2.5 h-2.5" /> {r.views || 0}
                                        </div>
                                        <div className="text-white text-[10.5px] font-extrabold line-clamp-2">
                                            {r.property?.title || "Property"}
                                        </div>
                                        <div className="text-white/70 text-[9px] truncate">{r.city || ""}</div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}
